/**
 * Gateway bar — horizontal strip showing all connected gateways.
 *
 * Displays compact chips for each gateway with:
 * - Color-coded status dot (green=connected, yellow=connecting, red=error)
 * - Gateway name
 * - Active/focused indicator (orange border)
 *
 * Tap a chip to focus that gateway for input.
 * Only shown when multiple gateways are configured.
 */

import { useMemo } from 'react';
import type { GatewayConfig, GatewayStatus } from '../types/gateway';
import styles from './GatewayBar.module.css';

interface GatewayBarProps {
  /** All configured gateways */
  gateways: GatewayConfig[];
  /** Currently focused gateway ID */
  activeGatewayId: string | null;
  /** Live connection states per gateway */
  connectionStates: Map<string, GatewayStatus>;
  /** Called when a gateway chip is tapped (switch focus) */
  onSelect: (id: string) => void;
}

/** Map status to dot class */
function statusClass(status: GatewayStatus): string {
  switch (status) {
    case 'connected':
      return styles.dotConnected;
    case 'connecting':
      return styles.dotConnecting;
    case 'pending':
      return styles.dotPending;
    case 'error':
    case 'origin_blocked':
      return styles.dotError;
    default:
      return styles.dotDisconnected;
  }
}

/** Abbreviate gateway name if too long */
function abbreviate(name: string, maxLen: number = 12): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen - 1) + '…';
}

export function GatewayBar({
  gateways,
  activeGatewayId,
  connectionStates,
  onSelect,
}: GatewayBarProps) {
  // Only render if there are multiple gateways
  const visible = gateways.length > 1;

  const chips = useMemo(
    () =>
      gateways.map((gw) => {
        const status = connectionStates.get(gw.id) ?? gw.status ?? 'disconnected';
        const isFocused = gw.id === activeGatewayId;
        return { ...gw, status, isFocused };
      }),
    [gateways, activeGatewayId, connectionStates],
  );

  if (!visible) return null;

  return (
    <div className={styles.bar}>
      {chips.map((chip) => (
        <button
          key={chip.id}
          className={`${styles.chip} ${chip.isFocused ? styles.chipFocused : ''}`}
          onClick={() => onSelect(chip.id)}
          title={`${chip.name} — ${chip.status}`}
        >
          <span className={`${styles.dot} ${statusClass(chip.status)}`} />
          <span className={styles.chipName}>{abbreviate(chip.name)}</span>
        </button>
      ))}
    </div>
  );
}
